import { 
  Droplets, 
  Ruler, 
  Scale, 
  CircleDot,
  Palette, 
  MapPin, 
  User, 
  Sparkles,
  ShieldCheck,
  History 
} from 'lucide-react'
import type { Teaware } from '@/entities/product'

export interface ProductTeawareDetailsProps {
  teaware: Teaware
}

/**
 * Виджет: Детали посуды (характеристики, материал, мастер, уход)
 */
export function ProductTeawareDetails({ teaware }: ProductTeawareDetailsProps) {
  const hasVolume = teaware.volumeMl != null && teaware.volumeMl > 0
  const hasDiameter = teaware.diameterMm != null && teaware.diameterMm > 0
  const hasHeight = teaware.heightMm != null && teaware.heightMm > 0
  const hasWeight = teaware.weightGrams != null && teaware.weightGrams > 0
  const hasMaterial = !!teaware.material?.trim()
  const hasOrigin = !!teaware.origin?.trim()
  const hasArtisan = !!teaware.artisan?.trim()
  const hasFeatures = !!teaware.features?.trim()
  const hasCare = !!teaware.careInstructions?.trim()
  const hasHistory = !!teaware.history?.trim()
  
  const hasSpecs = hasVolume || hasDiameter || hasHeight || hasWeight
  const hasDetails = hasMaterial || hasOrigin || hasArtisan
  const hasContent = hasFeatures || hasCare || hasHistory
  
  if (!hasSpecs && !hasDetails && !hasContent) {
    return null
  }
  
  return (
    <div className="px-4 py-6 space-y-6">
      {/* Размеры и объём */}
      {hasSpecs && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-ink dark:text-white">Размеры</h2>
          <div className="grid grid-cols-2 gap-3">
            {hasVolume && (
              <div className="flex items-center gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
                <Droplets className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Объём</p>
                  <p className="text-lg font-semibold text-ink dark:text-white">{teaware.volumeMl} мл</p>
                </div>
              </div>
            )}
            
            {hasDiameter && (
              <div className="flex items-center gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
                <CircleDot className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Диаметр</p>
                  <p className="text-lg font-semibold text-ink dark:text-white">{teaware.diameterMm} мм</p>
                </div>
              </div>
            )}
            
            {hasHeight && (
              <div className="flex items-center gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
                <Ruler className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Высота</p>
                  <p className="text-lg font-semibold text-ink dark:text-white">{teaware.heightMm} мм</p>
                </div>
              </div>
            )}
            
            {hasWeight && (
              <div className="flex items-center gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
                <Scale className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Вес</p>
                  <p className="text-lg font-semibold text-ink dark:text-white">{teaware.weightGrams} г</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Материал, происхождение, мастер */}
      {hasDetails && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-ink dark:text-white">О изделии</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {hasMaterial && (
              <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
                <Palette className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Материал</p>
                  <p className="text-base font-medium text-ink dark:text-white">{teaware.material}</p>
                </div>
              </div>
            )}
            
            {hasOrigin && (
              <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
                <MapPin className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Происхождение</p>
                  <p className="text-base font-medium text-ink dark:text-white">{teaware.origin}</p>
                </div>
              </div>
            )}
            
            {hasArtisan && (
              <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border sm:col-span-2">
                <User className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Мастер</p>
                  <p className="text-base font-medium text-ink dark:text-white">{teaware.artisan}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Особенности */}
      {hasFeatures && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary dark:text-primary-light" />
            <h2 className="text-xl font-semibold text-ink dark:text-white">Особенности</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {teaware.features}
            </p>
          </div>
        </div>
      )}
      
      {/* История изделия */}
      {hasHistory && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary dark:text-primary-light" />
            <h2 className="text-xl font-semibold text-ink dark:text-white">История</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {teaware.history}
            </p>
          </div>
        </div>
      )}
      
      {/* Уход за изделием */}
      {hasCare && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary dark:text-primary-light" />
            <h2 className="text-xl font-semibold text-ink dark:text-white">Уход</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {teaware.careInstructions}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

